import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY_MS = 350;

function hasHover() {
  // Ohne matchMedia (jsdom) gilt das Gerät als hover-fähig.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(hover: none)').matches;
}

/**
 * Die eine schwebende Kartenvorschau des Playtest-Bretts (Spec 2026-10-21 §5.2): Zeiger auf
 * einer Karte, nach `delayMs` erscheint die Vorschau; Verlassen, Drücken (Tappen, Ziehen) oder
 * `clear()` blendet sie sofort aus.
 *
 *   const hover = useHoverPreview({ delayMs: 350 });
 *   <div {...hover.bind(card, { placement: 'above' })}>…</div>
 *   <FloatingCardPreview preview={hover.preview} />
 *
 * `preview` ist `{ card, anchorRect, placement, pointer, otherSide }` oder null: `anchorRect` misst das
 * Element unter dem Zeiger erst beim Erscheinen (der Handfächer ist dann schon angehoben),
 * `pointer` ist die letzte Zeigerposition davor. Die Vorschau folgt dem Zeiger danach nicht —
 * jede Zeigerbewegung wäre sonst ein Neu-Rendern der ganzen Seite. Hängt das Element unter dem
 * Zeiger nicht mehr im Dokument (Karte gespielt, Kachel ausgeblendet), ist `preview` beim
 * nächsten Rendern null — ein `pointerleave` kommt von einem entfernten Element nie.
 *
 * Stellen, deren Karte wechselt, während das Element bleibt (Oberkarte einer Zonen-Kachel, einer
 * offenen Bibliothek), binden mit `{ key }` und auch ohne Karte (`bind(null, { key })` liefert
 * leere Handler): wechselt die Karte unter diesem Schlüssel, solange ihre Vorschau steht, zeigt
 * die Vorschau die neue Karte, ohne Karte verschwindet sie. Ohne `key` gilt `card.instanceId`.
 * Ersetzt wird bei jedem anderen Objekt unter demselben Schlüssel, nicht nur bei anderer
 * `instanceId`: ändert der Server die Karte (z. B. Transformieren), zeigt die Vorschau das neue
 * Objekt. Deshalb binden zwei Stellen denselben Schlüssel nur mit demselben Objekt (Hand und
 * Mulligan-Überlagerung binden dieselben Objekte aus `me.hand`); binden zwei Stellen verschiedene
 * Objekte unter einem Schlüssel, ersetzt jede das Objekt der anderen, und jedes Ersetzen löst ein
 * weiteres Rendern aus — eine Kette von Neu-Rendern ohne Ende.
 *
 * `otherSide` sagt, ob die Vorschau die andere Seite der Karte zeigt: es startet bei jedem
 * Erscheinen `false`, `toggleSide()` kehrt es um (ohne stehende Vorschau passiert nichts). Beim
 * Ersetzen bleibt es nur, wenn das neue Objekt dieselbe Karte ist (gleiche `instanceId`);
 * übernimmt eine andere Karte den Schlüssel, startet sie mit ihrer echten Seite. Ausblenden setzt
 * es zurück.
 *
 * Geräte ohne Hover (`(hover: none)`) bekommen von `bind` leere Handler: Touch-Browser
 * synthetisieren beim Antippen Zeiger-Ereignisse, die Vorschau erschiene sonst beim Tipp. Aus
 * demselben Grund ignorieren die Handler Touch-Zeiger auf Geräten mit Maus und Touchscreen.
 */
export function useHoverPreview({ delayMs = DEFAULT_DELAY_MS } = {}) {
  const [preview, setPreview] = useState(null);
  const [enabled] = useState(hasHover);
  const timerRef = useRef(null);
  const pointerRef = useRef(null);
  const anchorRef = useRef(null);
  const shownRef = useRef({ key: undefined, card: null });

  const cancelTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const clear = useCallback(() => {
    cancelTimer();
    shownRef.current = { key: undefined, card: null };
    setPreview(null);
  }, [cancelTimer]);

  const toggleSide = useCallback(() => {
    setPreview((current) => (current ? { ...current, otherSide: !current.otherSide } : current));
  }, []);

  useEffect(() => cancelTimer, [cancelTimer]);

  const bind = useCallback((card, { placement = 'auto', key } = {}) => {
    if (!enabled) return {};
    const bindKey = key ?? card?.instanceId;
    const shown = shownRef.current;
    if (bindKey !== undefined && bindKey === shown.key && card !== shown.card) {
      // bind läuft während des Renderns; der Zustand folgt danach, nicht mitten darin.
      shownRef.current = { key: bindKey, card };
      Promise.resolve().then(() => setPreview((current) => {
        if (!current) return current;
        if (!card) return null;
        const sameCard = card.instanceId === current.card?.instanceId;
        return { ...current, card, otherSide: sameCard ? current.otherSide : false };
      }));
    }
    if (!card) return {};
    return {
      onPointerEnter: (e) => {
        if (e.pointerType === 'touch') return;
        const target = e.currentTarget;
        pointerRef.current = { x: e.clientX, y: e.clientY };
        cancelTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (!target?.isConnected) return;
          anchorRef.current = target;
          shownRef.current = { key: bindKey, card };
          setPreview({
            card,
            anchorRect: target.getBoundingClientRect(),
            placement,
            pointer: pointerRef.current,
            otherSide: false,
          });
        }, delayMs);
      },
      onPointerMove: (e) => {
        if (e.pointerType === 'touch') return;
        pointerRef.current = { x: e.clientX, y: e.clientY };
      },
      onPointerLeave: clear,
      onPointerDown: clear,
    };
  }, [enabled, delayMs, cancelTimer, clear]);

  const visible = preview && anchorRef.current?.isConnected ? preview : null;
  return { preview: visible, bind, clear, toggleSide };
}
